import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import GET_ASSET from 'features/editor/graphql/query/getAsset';

export type MetaDataField = {
  key: string;
  value: string;
};

export type Asset = {
  itemType: string;
  mAssetId: string;
  mContentKey?: string;
  mContentUrl?: string;
  mId: string;
  mMetaData: MetaDataField[];
  mRefId: string;
  mThumbnailUrl?: string;
  mTitle: string;
  mType: string;
  mUpdatedAt: string;
  mediaType: string;
  assetUri?: null;
};

const noop = () => {};

interface GetAsset {
  getAsset: Asset;
}

/**
 * This hook fetches an asset lazily, starting with 'cache-only' policy and then refetch
 * from the network after a delay.
 * The delay is 300ms if no data is initially found in the cache and 3s if data is found.
 * @param mId    The mId of the asset
 * @param mRefId The mRefId of the asset
 * @param skip   Whether to skip the query
 * @returns      An object containing:
 *               `asset` - The fetched asset or `null` if not available.
 *               `error` - The error encountered during the fetch or `null` if none.
 *               `loading` - A boolean indicating whether the fetch is in progress.
 */
export default function useLazyGetAsset(
  mId?: string,
  mRefId?: string,
  skip = false,
) {
  const variables = useMemo(() => ({ input: { mId, mRefId } }), [mId, mRefId]);
  const shouldSkip = skip || !mId || !mRefId;

  const { data, error, loading, refetch } = useQuery<GetAsset>(GET_ASSET, {
    variables,
    fetchPolicy: 'cache-only',
    nextFetchPolicy: 'cache-first',
    skip: shouldSkip,
  });

  const hasData = !!data?.getAsset;
  const hasFetchedFromNetworkRef = useRef(false);

  useEffect(() => {
    if (shouldSkip) return;

    hasFetchedFromNetworkRef.current = false;
    const delay = hasData ? 3000 : 300;

    const timer = setTimeout(() => {
      if (!hasFetchedFromNetworkRef.current) {
        hasFetchedFromNetworkRef.current = true;
        refetch().catch(noop);
      }
    }, delay);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [variables, hasData, shouldSkip, refetch]);

  if (shouldSkip) return { asset: null, error: null, loading: false } as const;

  const asset = data?.getAsset;

  return { asset, error, loading } as const;
}
